// src/components/ProductsSection.js
import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FiChevronRight, FiShield, FiTrash2 } from 'react-icons/fi';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import bgProductsImg from '../assets/bg-products.webp';

const isLightSurface = (color = '') => {
  const normalized = color.trim().toLowerCase();
  return normalized === '#fff' || normalized === '#ffffff' || normalized === 'white';
};

/* ────── Keyframes ────── */
const pulsingGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(76, 175, 80, 0.3); }
  50% { box-shadow: 0 0 16px rgba(76, 175, 80, 0.5); }
`;

/* ────── Sección ────── */
const SectionContainer = styled.section`
  padding: var(--spacing-xxl) 0;
  position: relative;
  overflow: hidden;
  background: #0B1D3A;
`;

/* Background image with blur and parallax */
const ParallaxBackground = styled.div`
  position: absolute;
  top: -200px;
  left: -100px;
  right: -100px;
  bottom: -200px;
  background: #0B1D3A url(${bgProductsImg}) center/cover no-repeat;
  filter: blur(4px);
  transform: translateY(var(--parallax-offset, 0px)) scale(1.3);
  will-change: transform;
  z-index: 0;
  
  /* Lighter overlay to let image show through */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(11, 29, 58, 0.3);
  }
`;

/* ────── Títulos ────── */
const SectionTitle = styled(motion.h2)`
  text-align: center;
  margin-bottom: var(--spacing-lg);
  position: relative;
  font-size: 2.8rem;
  font-weight: 800;
  color: var(--white);

  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, var(--accent), rgba(255, 255, 255, 0.5));
    border-radius: 2px;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #fff 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline;
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  max-width: 680px;
  margin: 0 auto var(--spacing-xl);
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  line-height: 1.7;
`;

/* ────── Contenedor interno ────── */
const ProductsWrapper = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 10;
  isolation: isolate;
`;

/* ────── Grid de productos ────── */
const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: var(--spacing-xl);

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

/* ────── Tarjeta de producto (Advanced Glassmorphism) ────── */
const ProductCard = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 24px;
  overflow: hidden;
  background: transparent;
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.5), 
              box-shadow 0.4s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25), 
              0 0 0 1px rgba(255, 255, 255, 0.05);

  /* Glass Filter Layer - blur + saturation */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    backdrop-filter: blur(16px) saturate(180%) brightness(1.05);
    -webkit-backdrop-filter: blur(16px) saturate(180%) brightness(1.05);
    z-index: 0;
  }

  /* Glass Overlay Layer - semi-transparent background */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${props => `
      linear-gradient(180deg, rgba(8, 18, 34, 0.34) 0%, rgba(8, 18, 34, 0.52) 100%),
      linear-gradient(120deg, rgba(255, 255, 255, 0.04) 0%, ${(props.accentColor || '#47B5FF')}16 100%)
    `};
    z-index: 1;
  }

  &:hover {
    transform: translateY(-12px) scale(1.02);
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35), 
                0 0 0 1px rgba(255, 255, 255, 0.1),
                0 0 40px ${props => `${props.accentColor}25` || 'rgba(71, 181, 255, 0.15)'};
  }
`;

/* Glass Specular Layer - light reflections */
const GlassSpecular = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  z-index: 2;
  box-shadow: 
    inset 1px 1px 0 rgba(255, 255, 255, 0.4),
    inset 0 0 20px rgba(255, 255, 255, 0.05),
    inset 0 -1px 0 rgba(0, 0, 0, 0.1);
`;

/* ────── Encabezado de la tarjeta ────── */
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-lg);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  position: relative;
  z-index: 3;
`;

const CardLogo = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  overflow: hidden;
  background: ${props => (
    isLightSurface(props.bgColor)
      ? 'linear-gradient(145deg, #ffffff 0%, #f4f7ff 58%, #fff1e8 100%)'
      : (props.bgColor || 'var(--primary)')
  )};
  border: 1px solid ${props => (isLightSurface(props.bgColor) ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.08)')};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--spacing-md);
  box-shadow: ${props => (
    isLightSurface(props.bgColor)
      ? `0 8px 22px rgba(0, 0, 0, 0.22), 0 0 0 1px ${(props.accentColor || '#E87830')}2E`
      : `0 4px 12px rgba(0, 0, 0, 0.3), 0 0 20px ${(props.accentColor || props.bgColor || '#47B5FF')}30`
  )};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    width: 46px;
    height: 46px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: ${props => (
      isLightSurface(props.bgColor)
        ? `radial-gradient(
            circle,
            ${(props.accentColor || '#E87830')}5C 0%,
            ${(props.accentColor || '#E87830')}30 40%,
            rgba(24, 66, 168, 0.22) 62%,
            rgba(255, 255, 255, 0) 82%
          )`
        : 'transparent'
    )};
    filter: blur(6px);
    opacity: 0.92;
    z-index: 0;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0.25) 0%,
      rgba(0, 0, 0, 0) 10%
    );
    z-index: 2;
    pointer-events: none;
  }

  img {
    width: 75%;
    height: 75%;
    object-fit: contain;
    transform: scale(${props => props.logoScale || 1});
    transform-origin: center;
    filter: ${props => (isLightSurface(props.bgColor) ? 'drop-shadow(0 3px 8px rgba(232, 120, 48, 0.24))' : 'none')};
    position: relative;
    z-index: 1;
  }

  svg {
    font-size: 1.8rem;
    color: #ffffff;
    position: relative;
    z-index: 1;
  }
`;

const CardHeaderInfo = styled.div`
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 10px 0;
  color: var(--white);
`;

/* ────── Etiquetas de estado ────── */
const StatusTag = styled.div`
  display: inline-block;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  background-color: ${props => `${props.bgColor}` || 'rgba(71, 181, 255, 0.1)'};
  color: ${props => props.textColor || 'var(--accent)'};
  border: 1px solid ${props => props.borderColor || 'var(--accent)'};
  position: relative;
  overflow: hidden;
`;

/* Pulsing glow for "Available" status - subtle and elegant */
const AnimatedStatusTag = styled(StatusTag)`
  animation: ${pulsingGlow} 3s ease-in-out infinite;
`;

/* ────── Contenido de la tarjeta ────── */
const CardContent = styled.div`
  padding: var(--spacing-lg);
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 3;
`;

const ProductDescription = styled.p`
  color: rgba(255, 255, 255, 0.88);
  line-height: 1.7;
  margin-bottom: var(--spacing-lg);
  font-size: 1.05rem;
`;

const FeatureList = styled.ul`
  margin-bottom: var(--spacing-lg);
  padding-left: 0;
  list-style-type: none;
`;

const FeatureItem = styled.li`
  color: rgba(255, 255, 255, 0.82);
  margin-bottom: 14px;
  padding-left: 28px;
  position: relative;
  line-height: 1.6;
  font-size: 0.95rem;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${props => props.accentColor || 'var(--accent)'};
    font-size: 0.9rem;
    font-weight: bold;
  }

`;

const CardFooter = styled.div`
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  background: rgba(255, 255, 255, 0.02);
  position: relative;
  z-index: 3;
`;

const FooterLinks = styled.div`
  display: flex;
  gap: var(--spacing-md);
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  padding-top: var(--spacing-sm);
  border-top: ${props => props.withDivider ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'};
`;

const DownloadButtons = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-md) 0;
`;

const StoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 8px;
  background: transparent;
  border: none;
  padding: 0;
  overflow: hidden;

  img {
    height: 100%;
    width: auto;
    display: block;
  }

  &:hover {
    transform: translateY(-4px) scale(1.02);
    filter: brightness(1.1);
  }

  &:active {
    transform: translateY(-2px) scale(1.01);
  }
`;

const DetailsLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: var(--spacing-md);
  font-weight: 600;
  font-size: 0.95rem;
  color: ${({ $color }) => $color || 'var(--accent)'};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const PrivacyLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  transition: color 0.2s ease;
  
  svg {
    margin-right: 6px;
    font-size: 0.9rem;
  }
  
  &:hover {
    color: var(--white);
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.5);
  }
`;

const ComingSoonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  align-items: center;
`;

const ComingSoonText = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.75);
  text-align: center;
  margin: 0;
  font-style: italic;
`;

const LearnMoreButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
  font-weight: 500;
  color: #ffffff;
  background: ${props => props.bgColor || 'var(--accent)'};
  padding: 12px 24px;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px ${props => `${props.bgColor}40` || 'rgba(71, 181, 255, 0.25)'};
  width: 100%;
  max-width: 200px;

  svg {
    margin-left: 8px;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props => `${props.bgColor}60` || 'rgba(71, 181, 255, 0.4)'};
    background: ${props => props.hoverColor || 'var(--primary)'};

    svg {
      transform: translateX(4px);
    }
  }

  &:active {
    transform: translateY(0);
  }
`;

/* ────── Animaciones ────── */
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

/* ────── Componente Product ────── */
const Product = ({
  logo,
  icon,
  title,
  description,
  features,
  status,
  bgColor,
  statusBgColor,
  statusTextColor,
  statusBorderColor,
  accentColor,
  learnMoreUrl,
  logoScale,
  detailsPath
}) => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const isExternalLearnMore = Boolean(learnMoreUrl && /^https?:\/\//i.test(learnMoreUrl));

  // Determinar los colores de estado según el tipo de estado
  const getStatusColors = () => {
    if (status === 'available') {
      return {
        bg: 'rgba(76, 175, 80, 0.1)',
        text: '#4CAF50',
        border: '#4CAF50'
      };
    }
    if (status === 'coming_soon') {
      return {
        bg: accentColor ? `${accentColor}24` : 'rgba(71, 181, 255, 0.14)',
        text: '#FFF4EA',
        border: accentColor || '#47B5FF'
      };
    }
    if (status === 'in_development') {
      return {
        bg: 'rgba(255, 107, 107, 0.1)',
        text: '#FF6B6B',
        border: '#FF6B6B'
      };
    }
    return { bg: 'rgba(71, 181, 255, 0.1)', text: '#47B5FF', border: '#47B5FF' };
  };

  const statusColors = getStatusColors();
  const statusText = status === 'available' ? 'Available Now' : (status === 'coming_soon' ? 'Waitlist Open' : 'In Development');

  return (
    <ProductCard
      ref={ref}
      variants={fadeIn}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      accentColor={accentColor || bgColor}
      status={status}
    >
      <GlassSpecular />
      <CardHeader>
        <CardLogo bgColor={bgColor} accentColor={accentColor || bgColor} logoScale={logoScale}>
          {logo ? <img src={logo} alt={`${title} logo`} /> : icon}
        </CardLogo>
        <CardHeaderInfo>
          <CardTitle>{title}</CardTitle>
          {status === 'available' ? (
            <AnimatedStatusTag
              bgColor={statusColors.bg}
              textColor={statusColors.text}
              borderColor={statusColors.border}
            >
              {statusText}
            </AnimatedStatusTag>
          ) : (
            <StatusTag
              bgColor={statusColors.bg}
              textColor={statusColors.text}
              borderColor={statusColors.border}
            >
              {statusText}
            </StatusTag>
          )}
        </CardHeaderInfo>
      </CardHeader>

      <CardContent>
        <ProductDescription>{description}</ProductDescription>
        <FeatureList>
          {features.map((feature, i) => (
            <FeatureItem key={i} accentColor={accentColor || bgColor}>
              {feature}
            </FeatureItem>
          ))}
        </FeatureList>
        {detailsPath && (
          <DetailsLink to={detailsPath} $color={title === 'HoldThen' ? '#F5A066' : '#B9A6F0'}>
            Learn more about {title} <FiChevronRight aria-hidden="true" />
          </DetailsLink>
        )}
      </CardContent>

      <CardFooter bgColor={bgColor}>
        {title === "VolpeTrack" && status === "available" && (
          <DownloadButtons>
            <StoreButton
              href="https://apps.apple.com/us/app/volpetrack-ai-budget-tracker/id6748869391"
              target="_blank"
              rel="noopener noreferrer"
              title="Download on the App Store"
            >
              <img src="/assets/app-store.svg" alt="Download VolpeTrack on the App Store" width="162" height="48" />
            </StoreButton>
            <StoreButton
              href="https://play.google.com/store/apps/details?id=com.volpetrack.mobile"
              target="_blank"
              rel="noopener noreferrer"
              title="Get it on Google Play"
            >
              <img src="/assets/google-play.svg" alt="Get VolpeTrack on Google Play" width="162" height="48" />
            </StoreButton>
          </DownloadButtons>
        )}
        {title === "VolpeTrack" && (
          <FooterLinks withDivider={status === "available"}>
            <PrivacyLink
              to="/volpetrack/privacy"
              hoverColor={accentColor}
              title="Privacy Policy"
            >
              <FiShield /> Privacy
            </PrivacyLink>
            <PrivacyLink
              to="/volpetrack/terms"
              hoverColor={accentColor}
              title="Terms & Conditions"
            >
              <FiShield /> Terms
            </PrivacyLink>
            <PrivacyLink
              to="/volpetrack/account-deletion"
              hoverColor={accentColor}
              title="Account Deletion"
            >
              <FiTrash2 /> Deletion
            </PrivacyLink>
          </FooterLinks>
        )}
        {status !== "available" && (
          <ComingSoonWrapper>
            {status === "in_development" && (
              <ComingSoonText>
                We're working hard to bring you this amazing experience. Stay tuned!
              </ComingSoonText>
            )}
            {status === "coming_soon" && (
              <ComingSoonText>
                The waitlist is open at holdthen.com. Join now for early access.
              </ComingSoonText>
            )}
            <LearnMoreButton
              href={learnMoreUrl || '/#products'}
              target={isExternalLearnMore ? '_blank' : undefined}
              rel={isExternalLearnMore ? 'noopener noreferrer' : undefined}
              bgColor={accentColor || bgColor}
              hoverColor={accentColor || bgColor}
            >
              {isExternalLearnMore ? 'Join the waitlist' : 'Learn more'} <FiChevronRight />
            </LearnMoreButton>
          </ComingSoonWrapper>
        )}
      </CardFooter>
    </ProductCard>
  );
};

/* ────── Componente principal ────── */
const ProductsSection = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  /* Parallax scroll for background image */
  useEffect(() => {
    const section = document.getElementById('products');
    if (!section) return;

    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const rect = section.getBoundingClientRect();
          const windowHeight = window.innerHeight;

          // Calculate parallax offset based on section position
          if (rect.top < windowHeight && rect.bottom > 0) {
            const progress = (windowHeight - rect.top) / (windowHeight + rect.height);
            const offset = (progress - 0.5) * 400; // -200px to +200px movement
            section.style.setProperty('--parallax-offset', `${offset}px`);
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // inicial
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const products = [
    {
      logo: '/logo-volpetrack.svg',
      title: 'VolpeTrack',
      description:
        'Your AI-powered financial companion. Track expenses, visualize habits, and achieve your financial goals with intelligent insights.',
      features: [
        'AI assistant with smart categorization',
        '3 plans: Free, Premium Pro & Founder Pioneer',
        'Gamification with achievements & badges',
        'Multi-currency & multi-language support',
      ],
      status: 'available',
      bgColor: '#694FA6',
      accentColor: '#694FA6',
      detailsPath: '/volpetrack'
    },
    {
      logo: '/logo-holdthen-icon.webp',
      title: 'HoldThen',
      description:
        'B2B international logistics platform that unifies package forwarding, consolidation, inventory, billing, and end-to-end shipment tracking in one seamless flow.',
      features: [
        'Warehouse reception & real-time inventory',
        'International consolidation & shipping',
        'Integrated billing & invoicing',
        'Full traceability for clients and operations',
      ],
      status: 'coming_soon',
      bgColor: '#FFFFFF',
      accentColor: '#E87830',
      learnMoreUrl: 'https://www.holdthen.com/',
      logoScale: 1.24,
      detailsPath: '/holdthen'
    },
  ];

  return (
    <SectionContainer id="products">
      <ParallaxBackground />
      <ProductsWrapper>
        <div ref={ref}>
          <SectionTitle
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={fadeIn}
          >
            Our <GradientText>Products</GradientText>
          </SectionTitle>
          <SectionSubtitle
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={fadeIn}
          >
            AI products and software systems built in-house, shipped to the
            App Store, Google Play, and the web.
          </SectionSubtitle>
        </div>

        <ProductsGrid
          as={motion.div}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={staggerContainer}
        >
          {products.map((p, i) => (
            <Product key={i} {...p} />
          ))}
        </ProductsGrid>
      </ProductsWrapper>
    </SectionContainer>
  );
};

export default ProductsSection;
